import { Link } from "react-router-dom";
import type { Repo } from "../shared/types/api";
import { bytes, timeAgo } from "../shared/utils/format";
import { Avatar, Empty, VisibilityBadge } from "./Ui";
import { StarButton } from "./StarButton";
import { Icon } from "./Icon";

export function RepoList({
  repos,
  empty,
  onStarChange,
  compact = false,
}: {
  repos: Repo[];
  empty?: string;
  onStarChange?: () => void;
  compact?: boolean;
}) {
  if (repos.length === 0) return <Empty title={empty ?? "No wikis yet"} />;
  return (
    <ul className={`repo-list${compact ? " compact" : ""}`}>
      {repos.map((repo) => (
        <li className="repo-item" key={repo.id}>
          <Link
            className="wiki-author-avatar"
            to={`/user/${repo.owner.username}`}
            aria-label={`Visit ${repo.owner.username}`}
          >
            <Avatar name={repo.owner.displayName || repo.owner.username} />
          </Link>
          <div className="repo-item-body">
            {!compact && (
              <div className="wiki-author-line">
                <Link to={`/user/${repo.owner.username}`}>
                  {repo.owner.displayName || repo.owner.username}
                </Link>
                <span>@{repo.owner.username}</span>
                <span>shared a wiki</span>
              </div>
            )}
            <div className="row wiki-row-title">
              <h3>
                <Link to={`/${repo.owner.username}/${repo.name}`}>
                  {compact && (
                    <span className="repo-owner">{repo.owner.username} / </span>
                  )}
                  {repo.name}
                </Link>
                <VisibilityBadge visibility={repo.visibility} />
                {repo.forkedFrom && <span className="badge">Fork</span>}
              </h3>
              <div className="right">
                <StarButton repo={repo} onChange={onStarChange} />
              </div>
            </div>
            {!compact && repo.description && (
              <div className="muted repo-description">{repo.description}</div>
            )}
            {!compact && repo.topics?.length ? (
              <div className="row" aria-label="Wiki topics">
                {repo.topics.map((topic) => (
                  <Link
                    className="badge"
                    key={topic}
                    to={`/explore?topic=${encodeURIComponent(topic)}`}
                  >
                    {topic}
                  </Link>
                ))}
              </div>
            ) : null}
            <div className="repo-meta">
              <span>
                <Icon name="file" />
                {repo.fileCount} {repo.fileCount === 1 ? "page" : "pages"}
              </span>
              {!compact && <span>{bytes(repo.sizeBytes)}</span>}
              {!compact && repo.forks > 0 && (
                <span>
                  <Icon name="fork" />
                  {repo.forks} {repo.forks === 1 ? "fork" : "forks"}
                </span>
              )}
              <time dateTime={repo.updatedAt} title={repo.updatedAt}>
                Updated {timeAgo(repo.updatedAt)}
              </time>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
